/**
 * Le risposte delle FAQ, e le voci dell'elenco del piano, sono testo semplice
 * nei file di contenuto, con tre sole convenzioni: ogni riga è un paragrafo,
 * le righe consecutive che iniziano con «- » formano un elenco, e le parole
 * tra `**` vanno in grassetto. Qui il testo diventa blocchi, che la pagina
 * rende come paragrafi, elenchi e `<strong>`, e il JSON-LD come HTML (i tag
 * che Google ammette nelle risposte di una FAQPage).
 */

export type AnswerBlock = { kind: 'p'; text: string } | { kind: 'ul'; items: string[] };

/** Un pezzo di riga: testo normale o in grassetto. */
export interface InlinePart {
  text: string;
  strong: boolean;
}

const LIST_ITEM = /^-\s+/;

export function answerBlocks(answer: string): AnswerBlock[] {
  const blocks: AnswerBlock[] = [];
  for (const raw of answer.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (LIST_ITEM.test(line)) {
      const item = line.replace(LIST_ITEM, '');
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'ul') last.items.push(item);
      else blocks.push({ kind: 'ul', items: [item] });
    } else {
      blocks.push({ kind: 'p', text: line });
    }
  }
  return blocks;
}

/** Le parti di una riga: i pezzi dispari dello split sono quelli tra `**`. */
export function inlineParts(text: string): InlinePart[] {
  return text
    .split(/\*\*(.+?)\*\*/)
    .map((part, index) => ({ text: part, strong: index % 2 === 1 }))
    .filter(part => part.text !== '');
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inlineToHtml = (text: string) =>
  inlineParts(text)
    .map(part =>
      part.strong ? `<strong>${escapeHtml(part.text)}</strong>` : escapeHtml(part.text)
    )
    .join('');

export function answerToHtml(answer: string): string {
  return answerBlocks(answer)
    .map(block =>
      block.kind === 'p'
        ? `<p>${inlineToHtml(block.text)}</p>`
        : `<ul>${block.items.map(item => `<li>${inlineToHtml(item)}</li>`).join('')}</ul>`
    )
    .join('');
}
